import React, { useEffect, useState } from 'react';
import { Calendar, Clock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AppointmentDetailDrawer } from '../AppointmentDetailDrawer';
import {
  ClinicalMilestoneTimeline,
  MilestoneEmpty,
} from './ClinicalMilestoneTimeline';
import VisitMilestoneCard from './VisitMilestoneCard';
import VisitHistoryAuditPanel from './VisitHistoryAuditPanel';
import { fetchConsultationAuditLog, fetchPatientVisits } from './patientHistory.api';

const ACCENT = '#071B34';
const TYPES = ['all', 'OP', 'IP', 'ER', 'CR'];

/**
 * Doctor Visit History — milestone timeline of completed visits.
 * Branded navy accent matches referral / IITH letterhead.
 */
export function VisitHistory({ patientId, activeConsultationId, initialTab }) {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAuditLog, setShowAuditLog] = useState(initialTab === 'audit');
  const [drawerApptId, setDrawerApptId] = useState(null);
  const [drawerIsYou, setDrawerIsYou] = useState(false);

  useEffect(() => {
    if (initialTab === 'audit') setShowAuditLog(true);
  }, [initialTab]);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    const visitType = typeFilter === 'all' ? null : typeFilter;
    fetchPatientVisits(patientId, { visitType })
      .then((rows) => { if (!cancelled) setVisits(rows); })
      .catch(() => { if (!cancelled) setVisits([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, typeFilter]);

  useEffect(() => {
    if (!activeConsultationId) {
      setAuditLog([]);
      return undefined;
    }
    let cancelled = false;
    fetchConsultationAuditLog(activeConsultationId)
      .then((rows) => { if (!cancelled) setAuditLog(rows); })
      .catch(() => { if (!cancelled) setAuditLog([]); });
    return () => { cancelled = true; };
  }, [activeConsultationId]);

  const isYou = (v) => (
    v.doctor_id === user?.external_doctor_id || v.doctor_id === user?.doctor_id
  );

  return (
    <div className="flex h-full flex-col" data-testid="visit-history-section">
      <div className="mb-3 flex flex-shrink-0 items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-bold text-[#071B34]" style={{ fontFamily: 'Manrope' }}>
            Visit History
          </h3>
          <p className="mt-0.5 text-[11px] text-[#9AADBE]">
            {visits.length} completed visit{visits.length === 1 ? '' : 's'}
          </p>
        </div>
        {activeConsultationId ? (
          <button
            type="button"
            onClick={() => setShowAuditLog((v) => !v)}
            className={
              showAuditLog
                ? 'flex items-center gap-1.5 rounded-full border border-[#071B34] bg-[#071B34] px-3 py-1.5 text-xs font-semibold text-white transition'
                : 'flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50'
            }
            data-testid="toggle-audit-log"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Audit Log{auditLog.length > 0 ? ` (${auditLog.length})` : ''}
          </button>
        ) : null}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1" data-testid="visit-type-filter">
        {TYPES.map((key) => {
          const active = typeFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTypeFilter(key)}
              className={
                active
                  ? 'rounded-full border border-[#071B34] bg-[#071B34] px-2.5 py-1 text-[11px] font-bold text-white transition'
                  : 'rounded-full border border-[#D5DEE8] bg-white px-2.5 py-1 text-[11px] font-bold text-[#5A6B7C] transition hover:border-[#071B34]'
              }
              data-testid={`type-filter-${key}`}
            >
              {key === 'all' ? 'All' : key}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {showAuditLog ? (
          <VisitHistoryAuditPanel entries={auditLog} />
        ) : loading ? (
          <div className="flex items-center justify-center py-12 text-[#9AADBE]" data-testid="visit-history-loading">
            <Clock className="mr-2 h-6 w-6 animate-pulse" />
            <span className="text-sm">Loading visits…</span>
          </div>
        ) : visits.length === 0 ? (
          <MilestoneEmpty
            icon={Calendar}
            text={typeFilter === 'all' ? 'No completed visits yet' : `No ${typeFilter} visits found`}
          />
        ) : (
          <ClinicalMilestoneTimeline accent={ACCENT} testId="visit-month-list">
            {visits.map((visit, idx) => (
              <VisitMilestoneCard
                key={visit.appointment_id || idx}
                visit={visit}
                index={idx}
                isYou={isYou(visit)}
                accent={ACCENT}
                onOpen={() => {
                  setDrawerApptId(visit.appointment_id);
                  setDrawerIsYou(isYou(visit));
                }}
              />
            ))}
          </ClinicalMilestoneTimeline>
        )}
      </div>

      {drawerApptId ? (
        <AppointmentDetailDrawer
          appointmentId={drawerApptId}
          isCurrentDoctor={drawerIsYou}
          onClose={() => setDrawerApptId(null)}
        />
      ) : null}
    </div>
  );
}

export default VisitHistory;
